import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { IDisciplineViewerList } from 'Data/interfaces/FederatedViewer/IDisciplineViewerList';
import { BimSelection, BimTreeNode, BimViewerHandle } from 'Data/interfaces/BimViewer/IBimViewerPort';
import { Bim3DToolbar } from 'Pages/Viewer/CoordlyViewer/components/Bim3DToolbar/Bim3DToolbar';
import { Bim3DTools, useBim3DTools } from 'Pages/Viewer/CoordlyViewer/3DViewer/useBim3DTools';
import ButtonDrawer from 'Components/UI/ButtonDrawer';
import { Icon } from 'Components/UI';
import IfcPropertiesDrawer from '../IfcPropertiesDrawer';
import ClashPanel, { CLASH_DRAWER_WIDTH } from '../ClashPanel';
import { useCoordly3DClashFeature } from 'Utils/ViewerUtils';
import { BimPanelProperties } from '../../ifcProperties';
import stylesDrawer from '../PropertiesDrawer/PropertiesDrawer.module.scss';
import { useFederatedIfcViewer } from './useFederatedIfcViewer';
import { resolveUrnToFile } from '../../federationUrn';
import { IViewpointEngine } from 'Services/viewpoint/IViewpointEngine';
import { IfcLiteViewpointEngine } from 'Services/viewpoint/IfcLiteViewpointEngine';

const VIEWER_VERSION = 'v1.0.0';

interface IFederatedIfcViewerProps {
  urns: string[];
  viewerListDisciplines: IDisciplineViewerList[];
  constructionSiteId: number;
  drawerOpen?: boolean;
  onEngineError?(detail: { code: string; message: string }): void;
  onModelLoadFailed?(urn: string): void;
  onLoadingUrnChange?(urn: string | null): void;
  // `modelIndex` endereça o data model deste modelo no motor — é o que a árvore
  // usa para não ler a hierarquia (nem selecionar) do arquivo errado.
  onModelTree?(urn: string, tree: BimTreeNode[], modelIndex?: number): void;
  // Seleção crua do motor. As árvores vivem fora daqui (na lista de modelos) e
  // precisam ouvir a cena para desmarcar quando o 3D desmarca — clique no vazio,
  // Esc ou seleção que passou para outro modelo.
  onSelectionChanged?(selection: BimSelection): void;
  onHandleReady?(handle: BimViewerHandle | null): void;
  /**
   * Publica o motor de viewpoint da sessão IFC. A página consome os mesmos dois
   * callbacks que o `InternalViewer` (Autodesk) já oferece: um para a modal de
   * criar atividade, outro para restaurar o viewpoint de um apontamento.
   */
  onViewpointEngineReady?(engine: IViewpointEngine | null): void;
}

// Viewer IFC federado (ifc-lite/WebGPU) — usado na sessão IFC no lugar do
// InternalViewer (Autodesk). Vários modelos na cena via a porta (addModel por urn).
export const FederatedIfcViewer: FC<IFederatedIfcViewerProps> = ({
  urns,
  viewerListDisciplines,
  constructionSiteId,
  drawerOpen,
  onEngineError,
  onModelLoadFailed,
  onLoadingUrnChange,
  onModelTree,
  onSelectionChanged,
  onHandleReady,
  onViewpointEngineReady,
}) => {
  // Namespace `viewer`: são as mesmas chaves do botão do painel no InternalViewer.
  const { t } = useTranslation('viewer');
  const { t: tFederated } = useTranslation('federatedViewer');
  const [viewerDiv, setViewerDiv] = useState<HTMLDivElement | null>(null);
  // O painel de propriedades vive aqui, e não na página, porque no caminho da
  // Autodesk quem o monta é o InternalViewer — que não existe na sessão IFC.
  const [selectedProperties, setSelectedProperties] = useState<BimPanelProperties | null>(null);
  const [propertiesVisible, setPropertiesVisible] = useState(false);
  const [sectionOpen, setSectionOpen] = useState(false);
  // Clash e propriedades ocupam o mesmo lugar: abrir um fecha o outro.
  const clashEnabled = useCoordly3DClashFeature();
  const [clashOpen, setClashOpen] = useState(false);
  const clashOpenRef = useRef(false);
  clashOpenRef.current = clashOpen;

  // Declarado antes do hook da cena: os callbacks abaixo são criados ali e só
  // disparam depois do render, quando `tools` já existe. É o mesmo laço que o
  // ViewerBody3D resolve assim — a cena precisa avisar as ferramentas, e as
  // ferramentas precisam do handle que a cena cria.
  const toolsRef = useRef<Bim3DTools | null>(null);

  // `modelIndex` de cada urn, colhido conforme o data model de cada modelo fica
  // pronto. O viewpoint precisa dos dois: o urn identifica o arquivo no
  // `BimFiles`, e o índice endereça o data model de onde sai o GlobalId.
  const modelIndexByUrn = useRef<Map<string, number>>(new Map());

  // A seleção CRUA do motor, não o espelho do `useBim3DTools`: só ela traz o
  // modelo de cada id (`modelIndices`), e sem isso um id repetido em outra
  // disciplina resolveria o GlobalId do arquivo errado.
  const rawSelection = useRef<BimSelection | null>(null);

  const handleModelTree = useCallback((
    urn: string,
    tree: BimTreeNode[],
    modelIndex?: number,
  ) => {
    if (modelIndex !== undefined) { modelIndexByUrn.current.set(urn, modelIndex); }
    onModelTree?.(urn, tree, modelIndex);
  }, [onModelTree]);

  const { loaded, phase, fitToView, handleRef } = useFederatedIfcViewer({
    viewerDiv,
    urns,
    viewerListDisciplines,
    constructionSiteId,
    onEngineError,
    onModelLoadFailed,
    onLoadingUrnChange,
    onModelTree: handleModelTree,
    onHandleReady,
    // Selecionou um elemento na cena: abre o painel junto, senão a seleção não
    // teria efeito visível nenhum fora do destaque no 3D.
    onSelectedProperties: (properties) => {
      setSelectedProperties(properties);
      // Com o clash aberto, a seleção vem de clicar num conflito: abrir as
      // propriedades por cima tiraria a lista da tela.
      if (properties && !clashOpenRef.current) { setPropertiesVisible(true); }
    },
    // Síncrono de propósito: as árvores reagem dentro do mesmo clique, e é o que
    // deixa o clique num nó contêiner (que limpa o destaque no 3D) marcar o nó
    // em vez de se apagar num render seguinte.
    onSelection: (selection) => {
      rawSelection.current = selection;
      toolsRef.current?.handleSelectionChanged(selection);
      onSelectionChanged?.(selection);
    },
    onMeasure: (state) => toolsRef.current?.handleMeasureChanged(state),
    onViewReset: () => toolsRef.current?.handleViewReset(),
    onProjection: (mode) => toolsRef.current?.handleProjectionChanged(mode),
    onSection: (section) => toolsRef.current?.handleSectionChanged(section),
  });

  // As ferramentas (isolar, ocultar, corte, medição, X-Ray) são as mesmas do
  // viewer único: o hook opera a cena pelos comandos da porta, então serve
  // qualquer cena — inclusive a federada, com vários modelos.
  const tools = useBim3DTools({ handleRef });
  toolsRef.current = tools;

  // O contexto é lido no momento da captura, não no da montagem: a cena muda
  // embaixo (modelo entra, seleção troca, elemento é oculto).
  const viewpointEngine = useMemo(() => new IfcLiteViewpointEngine(
    () => ({
      handle: handleRef.current,
      models: urns.map((urn) => ({
        urn,
        name: resolveUrnToFile(urn, viewerListDisciplines)?.Name ?? urn,
        modelIndex: modelIndexByUrn.current.get(urn) ?? 0,
        isVisible: true,
      })),
      selection: {
        expressIds: rawSelection.current?.expressIds ?? [],
        modelIndices: rawSelection.current?.modelIndices,
      },
      hiddenIds: Array.from(tools.hiddenIds),
    }),
    () => (viewerDiv ? viewerDiv.clientWidth / viewerDiv.clientHeight : 1),
  ), [handleRef, urns, viewerListDisciplines, tools.hiddenIds, viewerDiv]);

  useEffect(() => {
    onViewpointEngineReady?.(viewpointEngine);
    return () => onViewpointEngineReady?.(null);
  }, [onViewpointEngineReady, viewpointEngine]);


  const clashModels = useMemo(() => urns.map((urn) => ({
    urn,
    name: resolveUrnToFile(urn, viewerListDisciplines)?.Name ?? urn,
  })), [urns, viewerListDisciplines]);

  const toggleProperties = () => {
    setPropertiesVisible((current) => !current);
    setClashOpen(false);
  };
  const toggleClash = () => {
    setClashOpen((current) => !current);
    setPropertiesVisible(false);
  };
  // Os botões acompanham a borda do drawer aberto (o de clash é mais largo).
  const drawerButtonsRight = clashOpen
    ? CLASH_DRAWER_WIDTH + 26
    : propertiesVisible ? 326 : 14;

  return (
    <>
      <Helmet>
        <link rel="stylesheet" type="text/css" href={`/coordly3DViewer/${VIEWER_VERSION}/css/loader.css`} />
      </Helmet>

      <div className={`app-shell embedded${drawerOpen ? ' drawer-open' : ''}`}>
        {phase !== 'idle' && (
          <div className="x-spinner-container">
            <div className="x-spinner"></div>
          </div>
        )}

        <main className="viewer-pane" style={{ position: 'relative', overflow: 'hidden' }}>
          <section className="viewer-canvas-area">
            <div id="bim-container" ref={setViewerDiv}></div>
          </section>

          {loaded && (
            <ButtonDrawer
              onClick={toggleProperties}
              style={{
                position: 'absolute',
                marginTop: 24,
                right: drawerButtonsRight,
                zIndex: 99,
              }}
              placement={propertiesVisible ? t('closePanel') : t('expandPanel')}
              border
              delay
            >
              <span className={stylesDrawer['iconToggleWrap']}>
                <Icon
                  icon="avancarDuplo"
                  customSize={10}
                  className={`${stylesDrawer['iconChildren']} ${propertiesVisible ? stylesDrawer['iconActive'] : ''}`}
                />
              </span>
            </ButtonDrawer>
          )}

          {loaded && clashEnabled && (
            <ButtonDrawer
              onClick={toggleClash}
              style={{
                position: 'absolute',
                marginTop: 64,
                right: drawerButtonsRight,
                zIndex: 99,
                background: clashOpen ? '#EAEDF0' : undefined,
              }}
              placement={tFederated('clash.title')}
              border
            >
              <Icon icon="alertTriangle" customSize={14} customColor={clashOpen ? '#324150' : '#8697A8'} />
            </ButtonDrawer>
          )}

          {propertiesVisible && (
            <IfcPropertiesDrawer
              properties={selectedProperties}
              visible={propertiesVisible}
              onClose={() => setPropertiesVisible(false)}
            />
          )}

          {loaded && clashEnabled && (
            <ClashPanel
              open={clashOpen}
              handleRef={handleRef}
              models={clashModels}
              onClose={() => setClashOpen(false)}
            />
          )}

          {/* Sem `treeOpen`/`onToggleTree`: no federado a árvore não é um painel
              do viewer, ela vive na lista de modelos (uma por modelo ligado). */}
          <Bim3DToolbar
            enabled={loaded}
            hasSelection={tools.hasSelection}
            onResetView={tools.resetView}
            sectionOpen={sectionOpen}
            section={tools.section}
            onFit={fitToView}
            onFitSelection={tools.frameSelection}
            onToggleSection={() => setSectionOpen((current) => !current)}
            onSectionChange={tools.setSection}
            measureMode={tools.measureMode}
            measurements={tools.measurements}
            onToggleMeasure={tools.toggleMeasureMode}
            onClearMeasurements={tools.clearMeasurements}
            projection={tools.projection}
            onProjectionChange={tools.setProjection}
          />
        </main>
      </div>
    </>
  );
};
